import { useSelector } from 'react-redux';
import { isS2Admin } from './globalFunctions';

class PermissionManager {
  constructor() {
    this.permissionCache = new Map();
  }

  // Get all permissions for a society and access type
  getPermissions(userHasRoleWithPermission, user, societyId, accessTypeId) {
    const cacheKey = `${societyId}-${accessTypeId}`;
    
    if (this.permissionCache.has(cacheKey)) {
      return this.permissionCache.get(cacheKey);
    }

    // If user is S2Admin, return all permissions
    if (isS2Admin(user)) {
      return {
        permissions: [],
        levelDataAccess: [],
        hierarchyIds:[],
        viewLevelsModel: [],
        hasAllAccess: true
      };
    }

    // Find the society-specific roles and permissions
    const societyWiseRolesAndPermissions = Array.isArray(userHasRoleWithPermission) 
      ? userHasRoleWithPermission.find(item => item["societyId"] === societyId)
      : userHasRoleWithPermission[societyId];

    // Find the role with matching accessTypeId
    const matchingRole = societyWiseRolesAndPermissions?.roles?.find(role => role.accessTypeId === accessTypeId);

    const permissions = {
      permissions: matchingRole?.permissions || [],
      levelDataAccess: matchingRole?.levelDataAccess || [],
      viewLevelsModel: matchingRole?.viewLevelsModel || [],
      hierarchyIds: matchingRole?.hierarchyIds || [],
      hasAllAccess: false
    };

    this.permissionCache.set(cacheKey, permissions);
    return permissions;
  }


  // Check if user has access to specific level data
  hasLevelDataAccess(userHasRoleWithPermission, user, societyId, accessTypeId, levelId) {
    const { levelDataAccess, hasAllAccess } = this.getPermissions(userHasRoleWithPermission, user, societyId, accessTypeId);
    return hasAllAccess || levelDataAccess.includes(levelId);
  }

  // Check if user can view specific level model
  canViewLevelModel(userHasRoleWithPermission, user, societyId, accessTypeId, levelId) {
    const { viewLevelsModel, hasAllAccess } = this.getPermissions(userHasRoleWithPermission, user, societyId, accessTypeId);
    return hasAllAccess || viewLevelsModel.includes(levelId);
  }

  // Check if user has access to specific hierarchy
  hasHierarchyAccess(userHasRoleWithPermission, user, societyId, accessTypeId, hierarchyId) {
    const { hierarchyIds, hasAllAccess } = this.getPermissions(userHasRoleWithPermission, user, societyId, accessTypeId);
    return hasAllAccess || hierarchyIds.includes(hierarchyId);
  }
  // Clear permission cache for a specific society or all
  clearCache(societyId = null) {
    if (societyId) {
      for (const key of this.permissionCache.keys()) {
        if (key.startsWith(`${societyId}-`)) {
          this.permissionCache.delete(key);
        } 
      }
    } else {
      this.permissionCache.clear();
    }
  }
}

// Create a singleton instance
const permissionManager = new PermissionManager();

// Custom hook to use the permission manager
export const useDataPermissions = (societyId, accessTypeId) => {
  const userHasRoleWithPermission = useSelector((state) => state.userHasRoleWithPermission);
  const user = useSelector((state) => state.user);
  
  const permissions = permissionManager.getPermissions(
    userHasRoleWithPermission,
    user,
    societyId,
    accessTypeId
  );
  
  return {
    hasLevelDataAccess: (levelId) => 
      permissionManager.hasLevelDataAccess(
        userHasRoleWithPermission,
        user,
        societyId,
        accessTypeId,
        levelId
      ),
    canViewLevelModel: (levelId) => 
      permissionManager.canViewLevelModel(
        userHasRoleWithPermission,
        user,
        societyId,
        accessTypeId,
        levelId
      ),
    permissions: permissions.permissions,
    levelDataAccess: permissions.levelDataAccess,
    viewLevelsModel: permissions.viewLevelsModel,
    hierarchyIds: permissions.hierarchyIds,
  };
};

export default permissionManager; 